/**
 * Editor nodes composable
 * Handles node insertion for contenteditable editor (images, tags, colors)
 */

import { nodeRegistry } from '../core/NodeRegistry';
import '../nodes/index';
import { normalizeColorToHex, isValidColor } from '../utils/material-utils';

/**
 * @param {Object} options
 * @param {import('vue').Ref<HTMLElement>} options.editorRef - Editor element ref
 * @param {Object} options.chatState - Chat state object
 * @param {Function} options.syncContent - Sync content callback
 * @param {Function} options.makeDraggable - Make element draggable function
 * @param {Function} options.addAssetToChatState - Add asset to chatState function
 * @param {Function} options.removeAssetFromChatState - Remove asset from chatState function
 * @param {Function} options.canAddMore - Check if can add more items function
 * @param {Function} options.toast - Toast notification function
 * @param {Object} options.limits - Limits for materials { MAX_IMAGES, MAX_TAGS, MAX_COLORS }
 * @param {Function} options.onColorClick - Callback when color tag is clicked (colorData, element, event) => void
 * @param {Function} options.onMaterialClick - Callback when material tag is clicked (materialData, element, event) => void
 * @returns {Object} Node insertion handlers
 */
export function useEditorNodes(options = {}) {
  const {
    editorRef,
    syncContent,
    makeDraggable,
    removeAssetFromChatState,
    canAddMore,
    toast,
    limits = {},
    onColorClick,
    onMaterialClick,
    onSkillClick,
  } = options;

  const { MAX_IMAGES = 20, MAX_DOCUMENTS = 20, MAX_TAGS = 10, MAX_COLORS = 10 } = limits;

  /**
   * Insert text at cursor position
   * @param {string} text
   */
  const insertTextAtCursor = (text) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Move cursor after inserted text
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  /**
   * Helper to update node by ID
   */
  const updateNodeById = (type, id, data, updateOptions = {}) => {
    if (!editorRef?.value) return false;

    let selector = '';
    if (type === 'image' || type === 'document') {
      selector = `[data-asset-id="${id}"]`;
    } else if (type === 'tag') {
      selector = `[data-tag-id="${id}"]`;
    } else if (type === 'color') {
      selector = `[data-color-id="${id}"]`;
    } else if (type === 'remix') {
      selector = `[data-remix-id="${id}"]`;
    } else if (type === 'skill') {
      selector = `[data-skill-id="${id}"]`;
    }

    const element = editorRef.value.querySelector(selector);
    if (element) {
      nodeRegistry.update(type, element, data, { ...options, ...updateOptions });
      return true;
    }
    return false;
  };

  /**
   * Create image element using NodeRegistry
   * @param {Object} asset
   * @returns {HTMLElement}
   */
  const createImageElement = (asset) => {
    return nodeRegistry.render('image', asset, {
      makeDraggable,
      onClick: onMaterialClick,
      onRemove: (assetId) => {
        removeAssetFromChatState?.(assetId);
        syncContent?.({ immediate: true });
      },
      onSync: (syncOptions = {}) => syncContent?.({ immediate: true, ...syncOptions }),
    });
  };

  /**
   * Insert image node (receives asset object)
   * @param {Object} asset
   * @param {Object} [insertionPoint] - Optional insertion point { target, position }
   * @returns {boolean} Whether the image was inserted successfully
   */
  const insertImageNode = (asset, insertionPoint = null) => {
    if (!editorRef?.value) return false;

    // Check image count limit
    if (canAddMore && !canAddMore('image')) {
      toast?.({
        title: 'Image limit reached',
        description: `You can add up to ${MAX_IMAGES} images`,
        variant: 'destructive',
      });
      return false;
    }

    // Ensure editor has focus
    editorRef.value.focus();

    const selection = window.getSelection();
    let range;

    if (insertionPoint && insertionPoint.target) {
      range = document.createRange();
      if (insertionPoint.position === 'before') {
        range.setStartBefore(insertionPoint.target);
      } else {
        range.setStartAfter(insertionPoint.target);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      // Put cursor at editor end
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const container = createImageElement(asset);
        editorRef.value.appendChild(container);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        syncContent?.({ immediate: true });
        return true;
      }
    }

    range = selection.getRangeAt(0);

    const container = createImageElement(asset);

    range.insertNode(container);

    // Add space after image
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(container);
    range.insertNode(space);

    // Move cursor after space
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }

    syncContent?.({ immediate: true });
    return true;
  };

  /**
   * Update image node (for replacing temp node with real asset)
   * @param {string} tempId
   * @param {Object} asset
   */
  const updateImageNode = (tempId, asset) => {
    updateNodeById('image', tempId, asset, {
      makeDraggable,
      onClick: onMaterialClick,
      onRemove: (assetId) => {
        removeAssetFromChatState?.(assetId);
        syncContent?.({ immediate: true });
      },
    });
    syncContent?.({ immediate: true });
  };

  /**
   * Remove image node by ID
   * @param {string} assetId
   */
  const removeImageNodeById = (assetId) => {
    if (!editorRef?.value) return;
    const element = editorRef.value.querySelector(`[data-asset-id="${assetId}"]`);
    if (element) {
      element.remove();
      syncContent?.({ immediate: true });
    }
  };

  /**
   * Insert tag
   * @param {Object} tag - Tag object { id, label, color }
   * @param {Object} options
   * @param {Function} options.hideMenu - Function to hide tag menu
   */
  const insertTag = (tag, insertOptions = {}) => {
    const { hideMenu } = insertOptions;

    // Check tag count limit
    if (canAddMore && !canAddMore('tag')) {
      toast?.({
        title: 'Tag limit reached',
        description: `You can add up to ${MAX_TAGS} tags`,
        variant: 'destructive',
      });
      hideMenu?.();
      return;
    }

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType === Node.TEXT_NODE) {
      const textBeforeCursor = textNode.textContent.slice(0, range.startOffset);
      const match = textBeforeCursor.match(/#([^\s#]*)$/);

      if (match) {
        // Delete #query part
        range.setStart(textNode, match.index);
        range.deleteContents();

        // Create tag element
        const tagEl = nodeRegistry.render('tag', tag, { makeDraggable });

        range.insertNode(tagEl);

        // Add space after tag
        const space = document.createTextNode('\u00A0');
        range.setStartAfter(tagEl);
        range.insertNode(space);

        // Move cursor
        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        hideMenu?.();
        syncContent?.({ immediate: true });
      }
    }
  };

  /**
   * Insert color Tag node (internal use, for inserting when parsing content)
   * @param {Object} colorData
   */
  const insertColorTagNode = (colorData) => {
    if (!editorRef?.value) return;

    const selection = window.getSelection();
    let range;

    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const colorTag = nodeRegistry.render('color', colorData, {
          makeDraggable,
          onClick: onColorClick,
        });
        editorRef.value.appendChild(colorTag);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        return;
      }
    }

    range = selection.getRangeAt(0);

    const colorTag = nodeRegistry.render('color', colorData, {
      makeDraggable,
      onClick: onColorClick,
    });

    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }
  };

  /**
   * Insert color Tag (can be called via method)
   * @param {string} colorValue
   */
  const insertColorTag = (colorValue) => {
    if (!editorRef?.value || !isValidColor(colorValue)) return;

    // Check color tag count limit
    if (canAddMore && !canAddMore('color')) {
      toast?.({
        title: 'Color limit reached',
        description: `You can add up to ${MAX_COLORS} color tags`,
        variant: 'destructive',
      });
      return;
    }

    // Ensure editor has focus
    editorRef.value.focus();

    const selection = window.getSelection();
    let range;

    const colorId = `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const normalized = normalizeColorToHex(colorValue);
    const colorData = { id: colorId, value: colorValue, displayValue: normalized };

    // If no valid selection or selection not in editor, create new selection
    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // If adding selection fails, append to editor end
        const colorTag = nodeRegistry.render('color', colorData, {
          makeDraggable,
          onClick: onColorClick,
        });
        editorRef.value.appendChild(colorTag);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        syncContent?.({ immediate: true });
        return;
      }
    }

    range = selection.getRangeAt(0);

    const colorTag = nodeRegistry.render('color', colorData, {
      makeDraggable,
      onClick: onColorClick,
    });

    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }

    syncContent?.({ immediate: true });
  };

  /**
   * Insert color Tag from input (auto-called when HEX color followed by space is detected)
   * @param {Text} textNode
   * @param {Range} range
   * @param {RegExpMatchArray} match
   * @param {Function} hideMenu - Function to hide tag menu
   */
  const insertColorTagFromInput = (textNode, range, match, hideMenu) => {
    // Check color tag count limit
    if (canAddMore && !canAddMore('color')) {
      toast?.({
        title: 'Color limit reached',
        description: `You can add up to ${MAX_COLORS} color tags`,
        variant: 'destructive',
      });
      syncContent?.();
      return;
    }

    const colorValue = `#${match[1]}`; // Full HEX color value
    const fullMatch = match[0]; // #RRGGBB + space
    const matchIndex = match.index;

    // Delete #RRGGBB + space
    range.setStart(textNode, matchIndex);
    range.setEnd(textNode, matchIndex + fullMatch.length);
    range.deleteContents();

    // Create color Tag
    const colorId = `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const normalized = normalizeColorToHex(colorValue);
    const colorData = { id: colorId, value: colorValue, displayValue: normalized };

    const colorTag = nodeRegistry.render('color', colorData, {
      makeDraggable,
      onClick: onColorClick,
    });

    // Insert color Tag
    range.insertNode(colorTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(colorTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    hideMenu?.();
    syncContent?.({ immediate: true });
  };

  /**
   * Insert Remix tag
   * @param {Object} remixData - Remix object { id, name }
   * @param {Object} insertOptions
   * @param {Function} insertOptions.hideMenu - Function to hide menu
   */
  const insertRemixTag = (remixData, insertOptions = {}) => {
    const { hideMenu } = insertOptions;

    // Ensure editor has focus
    if (editorRef?.value) {
      editorRef.value.focus();
    }

    const selection = window.getSelection();
    let range;

    const createRemixNode = () => nodeRegistry.render('remix', remixData, { makeDraggable });

    // If no valid selection or selection not in editor, create new selection
    if (
      !selection.rangeCount ||
      (editorRef?.value && !editorRef.value.contains(selection.anchorNode))
    ) {
      range = document.createRange();
      if (editorRef?.value) {
        range.selectNodeContents(editorRef.value);
        range.collapse(false);
      }
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // Fallback: append
        if (editorRef?.value) {
          const remixTag = createRemixNode();
          editorRef.value.appendChild(remixTag);
          editorRef.value.appendChild(document.createTextNode('\u00A0'));
          syncContent?.({ immediate: true });
        }
        return;
      }
    } else {
      range = selection.getRangeAt(0);
    }

    // Create remix Tag
    const remixTag = createRemixNode();

    range.insertNode(remixTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(remixTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore
    }

    hideMenu?.();
    syncContent?.({ immediate: true });
  };

  /**
   * Insert Remix Tag from input (auto-called when pattern matches)
   * @param {Text} textNode
   * @param {Range} range
   * @param {RegExpMatchArray} match
   */
  const insertRemixTagFromInput = (textNode, range, match) => {
    const fullMatch = match[0];
    const remixId = match[1];
    const remixName = match[2];
    const matchIndex = match.index;

    // Delete matched text
    range.setStart(textNode, matchIndex);
    range.setEnd(textNode, matchIndex + fullMatch.length);
    range.deleteContents();

    // Create remix Tag
    const remixTag = nodeRegistry.render(
      'remix',
      { id: remixId, name: remixName },
      { makeDraggable }
    );

    // Insert
    range.insertNode(remixTag);

    // Add space after
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(remixTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    syncContent?.({ immediate: true });
  };

  /**
   * Insert User Skill tag
   * @param {Object} skillData - Skill object { id }
   * @param {Object} insertOptions
   * @param {Function} insertOptions.hideMenu - Function to hide menu
   */
  const insertUserSkillTag = (skillData, insertOptions = {}) => {
    const { hideMenu } = insertOptions;

    // Ensure editor has focus
    if (editorRef?.value) {
      editorRef.value.focus();
    }

    const selection = window.getSelection();
    let range;

    const createSkillNode = () =>
      nodeRegistry.render('skill', skillData, {
        makeDraggable,
        onClick: onSkillClick,
        onRemove: () => {
          // We use removeUserSkillTag helper or direct remove
          // For SkillNode, it handles remove itself via callback?
          // SkillNode calls onRemove(data, container)
          // We need to implement removal logic here
          removeUserSkillTag(skillData.id);
        },
      });

    // If no valid selection or selection not in editor, create new selection
    if (
      !selection.rangeCount ||
      (editorRef?.value && !editorRef.value.contains(selection.anchorNode))
    ) {
      range = document.createRange();
      if (editorRef?.value) {
        range.selectNodeContents(editorRef.value);
        range.collapse(false);
      }
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // Fallback: append
        if (editorRef?.value) {
          const skillTag = createSkillNode();
          editorRef.value.appendChild(skillTag);
          editorRef.value.appendChild(document.createTextNode('\u00A0'));
          syncContent?.({ immediate: true });
        }
        return;
      }
    } else {
      range = selection.getRangeAt(0);
    }

    // Create skill Tag
    const skillTag = createSkillNode();

    range.insertNode(skillTag);

    // Add space after Tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(skillTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore
    }

    hideMenu?.();
    syncContent?.({ immediate: true });
  };

  /**
   * Insert User Skill Tag from input (auto-called when pattern matches)
   * @param {Text} textNode
   * @param {Range} range
   * @param {RegExpMatchArray} match
   */
  const insertUserSkillTagFromInput = (textNode, range, match) => {
    const fullMatch = match[0];
    const skillId = match[1];
    const matchIndex = match.index;

    // Delete matched text
    range.setStart(textNode, matchIndex);
    range.setEnd(textNode, matchIndex + fullMatch.length);
    range.deleteContents();

    // Create skill Tag
    const skillTag = nodeRegistry.render(
      'skill',
      { id: skillId },
      {
        makeDraggable,
        onClick: onSkillClick,
        onRemove: () => {
          removeUserSkillTag(skillId);
        },
      }
    );

    // Insert
    range.insertNode(skillTag);

    // Add space after
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(skillTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    syncContent?.({ immediate: true });
  };

  /**
   * Update material tag (update both editor node and chatState)
   * @param {string} assetId
   * @param {string} tag
   * @param {boolean} isMaterial
   * @param {HTMLElement} [targetNode] - Optional target node to update
   */
  const updateMaterialTag = (assetId, tag, isMaterial, targetNode) => {
    // If targetNode provided, update it directly
    if (targetNode) {
      // We can use nodeRegistry.update but we need the current data to update only tag
      // MaterialNode.render takes full asset data.
      // MaterialNode.update takes full asset data.
      // Here we only have tag.
      // We need to read existing data from attributes?
      // EditorNode doesn't have a "read" method.
      // We might need to assume we pass full data or MaterialNode handles partial updates.
      // MaterialNode.update expects { ...asset, tag, isMaterial }.

      // Let's check MaterialNode.render. It expects `data` to have { id, url, name, tag, isMaterial, width, height, loading }.
      // We can reconstruct data from attributes.

      const id = targetNode.getAttribute('data-asset-id');
      const url = targetNode.getAttribute('data-url');
      const name = targetNode.getAttribute('data-filename');
      const width = targetNode.getAttribute('data-width');
      const height = targetNode.getAttribute('data-height');
      const loading = targetNode.getAttribute('data-loading') === 'true';

      const newData = {
        id,
        url,
        name,
        tag,
        isMaterial,
        width,
        height,
        loading,
      };

      nodeRegistry.update('image', targetNode, newData, {
        makeDraggable,
        onClick: onMaterialClick,
        onRemove: (id) => removeAssetFromChatState?.(id),
        onSync: (opts) => syncContent?.(opts),
      });
      syncContent?.({ immediate: true });
      return true;
    }

    const element = editorRef?.value?.querySelector(`[data-asset-id="${assetId}"]`);
    if (element) {
      const id = element.getAttribute('data-asset-id');
      const url = element.getAttribute('data-url');
      const name = element.getAttribute('data-filename');
      const width = element.getAttribute('data-width');
      const height = element.getAttribute('data-height');
      const loading = element.getAttribute('data-loading') === 'true';
      // We use the NEW tag and isMaterial

      const newData = {
        id,
        url,
        name,
        tag,
        isMaterial,
        width,
        height,
        loading,
      };

      nodeRegistry.update('image', element, newData, {
        makeDraggable,
        onClick: onMaterialClick,
        onRemove: (id) => removeAssetFromChatState?.(id),
        onSync: (opts) => syncContent?.(opts),
      });
      syncContent?.({ immediate: true });
      return true;
    }

    return false;
  };

  /**
   * Update color tag value
   * @param {string} colorId - Color tag ID
   * @param {string} newValue - New color value
   */
  const updateColorTag = (colorId, newValue) => {
    const normalized = normalizeColorToHex(newValue);
    updateNodeById(
      'color',
      colorId,
      { id: colorId, value: newValue, displayValue: normalized },
      { makeDraggable, onClick: onColorClick }
    );
    syncContent?.({ immediate: true });
  };

  /**
   * Remove color tag by ID
   * @param {string} colorId - Color tag ID
   */
  const removeColorTag = (colorId) => {
    if (!editorRef?.value) return;
    const element = editorRef.value.querySelector(`[data-color-id="${colorId}"]`);
    if (element) {
      element.remove();
      syncContent?.({ immediate: true });
    }
  };

  /**
   * Remove user skill tag
   * @param {string} skillId
   */
  const removeUserSkillTag = (skillId) => {
    if (!editorRef?.value) return;
    const element = editorRef.value.querySelector(`[data-skill-id="${skillId}"]`);
    if (element) {
      element.remove();
      syncContent?.({ immediate: true });
    }
  };

  /**
   * Insert Document tag
   * @param {Object} asset - Document object { id, filename }
   * @param {Object} [insertionPoint] - Optional insertion point { target, position }
   */
  const insertDocumentTag = (asset, insertionPoint = null) => {
    if (!editorRef?.value) return;

    // Check document count limit
    if (canAddMore && !canAddMore('document')) {
      toast?.({
        title: 'Document limit reached',
        description: `You can add up to ${MAX_DOCUMENTS} items`,
        variant: 'destructive',
      });
      return;
    }

    // Ensure editor has focus
    editorRef.value.focus();

    const tagOptions = {
      makeDraggable,
      onRemove: (removedAsset, tagEl) => {
        tagEl.remove();
        removeAssetFromChatState?.(removedAsset.id);
        syncContent?.({ immediate: true });
      },
    };

    const selection = window.getSelection();
    let range;

    const createDocNode = () => nodeRegistry.render('document', asset, tagOptions);

    if (insertionPoint && insertionPoint.target) {
      range = document.createRange();
      if (insertionPoint.position === 'before') {
        range.setStartBefore(insertionPoint.target);
      } else {
        range.setStartAfter(insertionPoint.target);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      try {
        selection.addRange(range);
      } catch {
        // Fallback: append
        const docTag = createDocNode();
        editorRef.value.appendChild(docTag);
        editorRef.value.appendChild(document.createTextNode('\u00A0'));
        syncContent?.({ immediate: true });
        return;
      }
    }

    range = selection.getRangeAt(0);

    const docTag = createDocNode();

    range.insertNode(docTag);

    // Add space after tag
    const space = document.createTextNode('\u00A0');
    range.setStartAfter(docTag);
    range.insertNode(space);

    // Move cursor
    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    try {
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }

    syncContent?.({ immediate: true });
  };

  /**
   * Update document tag parsed status in the editor
   * @param {string} assetId - The document asset ID
   * @param {boolean} parsed - The new parsed status
   */
  const updateDocumentParsed = (assetId, parsed) => {
    // We need to fetch other attributes too?
    // DocumentNode.render needs { id, filename, parsed, loading }
    const element = editorRef?.value?.querySelector(`[data-asset-id="${assetId}"]`);
    if (element) {
      const id = assetId;
      const filename = element.getAttribute('data-filename');

      // For now, assume loading is false if we are updating parsed status (usually means parsing is done).

      nodeRegistry.update(
        'document',
        element,
        { id, filename, parsed, loading: false },
        {
          makeDraggable,
          onRemove: (removedAsset, tagEl) => {
            tagEl.remove();
            removeAssetFromChatState?.(removedAsset.id);
            syncContent?.({ immediate: true });
          },
        }
      );
    }
  };

  /**
   * Update document tag after upload completes (replace temp id with real asset)
   * @param {string} tempId - The temporary asset ID
   * @param {Object} asset - The real asset object from server
   */
  const updateDocumentNode = (tempId, asset) => {
    updateNodeById('document', tempId, asset, {
      makeDraggable,
      onRemove: (removedAsset, tagEl) => {
        tagEl.remove();
        removeAssetFromChatState?.(removedAsset.id);
        syncContent?.({ immediate: true });
      },
    });
    syncContent?.({ immediate: true });
  };

  /**
   * Get tag class name (Helper for external use if needed, but TagNode handles it)
   * Keeping it for backward compatibility if used elsewhere?
   * It was exported before.
   */
  const getTagClassName = (color) => {
    // Create a dummy TagNode to access its method? Or make it static?
    // Or just duplicate logic.
    // Since it was exported, I should probably keep it or check if it's used.
    // It's used in `TagNode.js` (I moved it there).
    // If other components use it, I should keep it.
    // But `useEditorNodes` exports it.

    const colorMap = {
      blue: 'bg-blue-100/60 text-black/80 ring-blue-200/60 hover:bg-blue-100/80',
      purple: 'bg-violet-100/60 text-black/80 ring-violet-200/60 hover:bg-violet-100/80',
      green: 'bg-emerald-100/60 text-black/80 ring-emerald-200/60 hover:bg-emerald-100/80',
      orange: 'bg-amber-100/60 text-black/80 ring-amber-200/60 hover:bg-amber-100/80',
      pink: 'bg-pink-100/60 text-black/80 ring-pink-200/60 hover:bg-pink-100/80',
      red: 'bg-rose-100/60 text-black/80 ring-rose-200/60 hover:bg-rose-100/80',
      indigo: 'bg-indigo-100/60 text-black/80 ring-indigo-200/60 hover:bg-indigo-100/80',
      teal: 'bg-teal-100/60 text-black/80 ring-teal-200/60 hover:bg-teal-100/80',
    };
    return `inline-flex items-center gap-1 mx-0.5 px-2.5 py-0.5 rounded-full text-[13px] font-medium ring-1 ring-inset select-none align-middle cursor-grab draggable-item transition-all duration-200 ${
      colorMap[color] || colorMap.blue
    }`;
  };

  return {
    insertTextAtCursor,
    insertImageNode,
    createImageElement,
    updateImageNode,
    removeImageNodeById,
    insertTag,
    insertColorTagNode,
    insertColorTag,
    insertColorTagFromInput,
    insertRemixTag,
    insertRemixTagFromInput,
    insertUserSkillTag,
    insertUserSkillTagFromInput,
    insertDocumentTag,
    updateDocumentNode,
    updateDocumentParsed,
    updateMaterialTag,
    updateColorTag,
    removeColorTag,
    removeUserSkillTag,
    getTagClassName,
  };
}
